import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import { ProcessTypeEnum } from '../../constants/enums';
import URLConstants from '../../constants/url-constants';
import config from '../../helpers/config-helper';
import {
  IAddProcess,
  IUpdateProcess,
} from '../../models/forms/IAddUpdateProcess';
import {
  IMultiSelectOption,
  IOption,
  IReactSelectOption,
} from '../../models/ICommon';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import {
  IProcessList,
  IProcessListVM,
  IProcessVM,
} from '../../models/response/IProcessResponse';
import { IProcessState } from '../../models/state/IProcessState';
import { formatMessage } from '../../translations/format-message';
import {
  GetAllProcessState,
  GetProcessState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
import { IAddProcessDimensionData } from '../../models/forms/IAddProcessDimensionData';
import {
  IDimensionDataItems,
  IProcessDimensionData,
} from '../../models/response/IProcessDimensionData';
import { da } from 'date-fns/locale';
import { IDimensionListVM } from '../../models/response/IDimensionResponse';
const appConfig = config();

interface IColumnProps {
  Id?: number;
  ShortName: string;
  IsHidden: boolean;
}

export class ProcessStore implements IProcessState {
  initialStateValue = ObservableInitialState;
  processList: IProcessListVM = GetAllProcessState;
  process: IProcessVM | undefined = undefined;
  processState = { ...this.initialStateValue };
  addUpdateProcessState = { ...this.initialStateValue };
  deleteProcessState = { ...this.initialStateValue };
  getAllProcessState = { ...this.initialStateValue };
  checkedColumns: IColumnProps[] = [];
  saveDimensionDataState = { ...this.initialStateValue };
  getProcessDimensionDataState = { ...this.initialStateValue };
  processDimension: IProcessDimensionData | undefined = undefined;
  getDimensionByProcessIdState = { ...this.initialStateValue };
  dimensionByProcessIdList: IDimensionListVM | undefined = undefined;

  constructor() {
    makeObservable(this, {
      process: observable,
      processState: observable,
      processList: observable,
      addUpdateProcessState: observable,
      deleteProcessState: observable,
      getAllProcessState: observable,
      GetProcessListService: action,
      GetProcessByIdService: action,
      AddProcessService: action,
      UpdateProcessService: action,
      DeleteProcessService: action,
      GetAllProcessService: action,
      resetDeleteProcess: action,
      resetAddUpdateProcessState: action,
      reset: action,
      allProcess: computed,
      processDetails: computed,
      allProcessReactSelectOptions: computed,
      allProcessOptions: computed,
      allProcessMulitSelectOptions: computed,
      selectedProcessDetails: computed,
      allFactoryProcess: computed,
      checkedColumns: observable,
      setCheckedColumns: action,
      saveDimensionDataState: observable,
      SaveDimensionDataService: action,
      resetSaveDimensionDataState: action,
      getProcessDimensionDataState: observable,
      GetProcessDimensionDataService: action,
      resetGetProcessDimensionDataState: action,
      processDimension: observable,
      getDimensionByProcessIdState: observable,
      dimensionByProcessIdList: observable,
      processMulitSelectOptions: computed
      // processDimensionData: computed,
    });
  }

  //#region Actions
  GetProcessListService = (
    currentPage: number,
    pagerSize: number,
    filterQuery: string
  ) => {
    this.getAllProcessState.inProgress = true;
    const url =
      URLConstants.GetAllProcessList +
      '?PageSize=' +
      pagerSize +
      '&PageNo=' +
      currentPage +
      '&filterQuery=' +
      filterQuery;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IProcessListVM>>) => {
        if (response.data.Error) {
          this.getAllProcessState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.processList = response.data.Data;
          this.getAllProcessState.success = true;
        }
      })
      .catch((err: string) => {
        this.getAllProcessState.error = err;
      })
      .finally(
        action(() => {
          this.getAllProcessState.inProgress = false;
        })
      );
  };

  AddProcessService = (data: IAddProcess) => {
    this.addUpdateProcessState.inProgress = true;
    const url = URLConstants.AddProcess;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateProcessState.error = response.data.Message;
        } else {
          this.addUpdateProcessState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateProcessState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateProcessState.inProgress = false;
        })
      );
  };

  UpdateProcessService = (data: IUpdateProcess) => {
    this.addUpdateProcessState.inProgress = true;
    let url = URLConstants.UpdateProcess;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateProcessState.error = response.data.Message;
        } else {
          this.addUpdateProcessState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateProcessState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateProcessState.inProgress = false;
        })
      );
  };

  GetProcessByIdService = (id: number) => {
    this.processState.inProgress = true;
    let url = URLConstants.GetProcessById + '/' + id;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IProcessVM>>) => {
        if (response.data.Error) {
          this.processState.error = response.data.Message;
        } else {
          this.process = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.processState.error = err;
      })
      .finally(
        action(() => {
          this.processState.inProgress = false;
        })
      );
  };

  DeleteProcessService = (id: number) => {
    this.deleteProcessState.inProgress = true;
    const url = URLConstants.DeleteProcess + '/' + id;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteProcessState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.deleteProcessState.success = true;
      })
      .catch((err: string) => {
        this.deleteProcessState.error = err;
      })
      .finally(
        action(() => {
          this.deleteProcessState.inProgress = false;
        })
      );
  };

  GetAllProcessService = (
    tenentId?: number,
    HideSettingKey?: string
    //  processOrder?: string
  ) => {
    // const ProcessOrder = processOrder === 'null' ? '' : processOrder;
    this.getAllProcessState.inProgress = true;
    let url = URLConstants.GetAllProcess;
    if (tenentId !== undefined) {
      url += `?tenantId=${tenentId}`;
    }
    if (HideSettingKey !== undefined) {
      url += `${tenentId !== undefined ? '&' : '?'}HideSettingKey=${HideSettingKey}`;
    }
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IProcessListVM>>) => {
        if (response.data.Error) {
          this.getAllProcessState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.processList = response.data.Data;
          this.processList.Processs.forEach((item) => {
            const exists = this.checkedColumns.some(
              (c) => c.ShortName === item.ShortName
            );
            if (!exists)
              this.checkedColumns.push({
                ShortName: item.ShortName,
                IsHidden: item.Hide || false,
              });
          });
        }
      })
      .catch((err: string) => {
        this.getAllProcessState.error = err;
      })
      .finally(
        action(() => {
          this.getAllProcessState.inProgress = false;
        })
      );
  };

  SaveDimensionDataService = (data: IAddProcessDimensionData) => {
    this.saveDimensionDataState.inProgress = true;
    const url = URLConstants.SaveProcessDimensionData;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.saveDimensionDataState.error = response.data.Message;
        } else {
          toast.success(formatMessage(response.data.Message));
          this.saveDimensionDataState.success = true;
        }
      })
      .catch((err: string) => {
        this.saveDimensionDataState.error = err;
      })
      .finally(
        action(() => {
          this.saveDimensionDataState.inProgress = false;
        })
      );
  };

  GetProcessDimensionDataService = (ProcessId: number) => {
    this.getProcessDimensionDataState.inProgress = true;
    const url = URLConstants.GetProcessDimensionData + '/' + ProcessId;
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IProcessDimensionData>>
        ) => {
          if (response.data.Error) {
            this.getProcessDimensionDataState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.processDimension = response.data.Data;
            this.getProcessDimensionDataState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.getProcessDimensionDataState.error = err;
      })
      .finally(
        action(() => {
          this.getProcessDimensionDataState.inProgress = false;
        })
      );
  };

  //#endregion

  //#region Computed
  setCheckedColumns = (data: IColumnProps[]) => {
    this.checkedColumns = [...data];
  };

  get allProcess(): IProcessList[] {
    let processData: IProcessList[] = [];
    if (this.processList?.Processs && this.processList.Processs?.length > 0) {
      this.processList.Processs.map((process) => {
        processData.push({
          Id: process.Id,
          ProcessName: process.ProcessName,
          Type: ProcessTypeEnum[process.Type],
          Comment: process.Comment || 'N/A',
          ShortName: process.ShortName,
        });
      });
    }
    return processData;
  }

  get processDetails(): IProcessVM {
    if (this.process) {
      return {
        Id: this.process.Id,
        ProcessName: this.process.ProcessName,
        Type: this.process.Type,
        Comment: this.process.Comment,
        ShortName: this.process.ShortName,
      };
    }
    return {
      Id: -1,
      ...GetProcessState,
    };
  }

  get selectedProcessDetails(): any {
    if (this.process) {
      return {
        Id: 0,
        ProcessId: this.process.Id,
        ProcessName: this.process.ProcessName,
        DueDates: 0,
        ProcessOrder: 1,
        Type: Number(this.process.Type),
        ShortName: this.process.ShortName,
      };
    }
    return {
      Id: -1,
      ...GetProcessState,
    };
  }

  get allFactoryProcess(): IProcessList[] {
    let processData: IProcessList[] = [];
    if (this.processList?.Processs && this.processList.Processs?.length > 0) {
      this.processList.Processs.map((process) => {
        if (typeof process.Type === 'number' && process.Type >= 0) {
          const result = this.checkedColumns.find(
            ({ ShortName }) => ShortName === process.ShortName
          );
          processData.push({
            Id: process.Id,
            ProcessName: process.ProcessName,
            Type: ProcessTypeEnum[process.Type],
            Comment: process.Comment || 'N/A',
            ShortName: process.ShortName,
            Hide: result?.IsHidden || false,
          });
        }
      });
    }
    return processData;
  }

  get allProcessReactSelectOptions(): IReactSelectOption[] {
    let processData: IReactSelectOption[] = [];
    processData.push({
      label: formatMessage('please_select'),
      value: '-1',
      isDisabled: true,
    });
    if (this.processList?.Processs && this.processList.Processs?.length > 0) {
      this.processList.Processs.map((process) => {
        processData.push({
          value: String(process.Id),
          label: process.ProcessName,
        });
      });
    }
    return processData;
  }

  get allProcessOptions(): IOption[] {
    let processData: IOption[] = [];
    processData.push({
      id: -1,
      value: 'please_select',
      disabled: false,
    });
    if (this.processList?.Processs && this.processList.Processs?.length > 0) {
      this.processList.Processs.map((process) => {
        processData.push({
          id: process.Id,
          value: process.ProcessName,
        });
      });
    }
    return processData;
  }

  get allProcessMulitSelectOptions(): IMultiSelectOption[] {
    let processData: IMultiSelectOption[] = [];
    if (this.processList?.Processs && this.processList.Processs?.length > 0) {
      processData.push({ label: 'Tag Assign', value: '-1' });
      this.processList.Processs.map((process) => {
        processData.push({
          value: String(process.Id),
          label: process.ProcessName,
        });
      });
      processData.push({ label: 'Tag Remove', value: '-2' });
    }
    return processData;
  }

  get processMulitSelectOptions(): IMultiSelectOption[] {
    let processData: IMultiSelectOption[] = [];
    if (this.processList?.Processs && this.processList.Processs?.length > 0) {
      // processData.push({ label: 'Tag Assign', value: '-1' });
      this.processList.Processs.map((process) => {
        processData.push({
          value: String(process.Id),
          label: process.ProcessName,
        });
      });
      // processData.push({ label: 'Tag Remove', value: '-2' });
    }
    return processData;
  }

  get processDimensionData(): IDimensionDataItems[] {
    let dimensionData: IDimensionDataItems[] = [];
    if (
      this.processDimension != undefined &&
      this.processDimension.ProcessDimensions &&
      this.processDimension.ProcessDimensions?.length > 0
    ) {
      this.processDimension.ProcessDimensions.map((data) => {
        dimensionData.push({
          Id: data.Id,
          Messages: data.Messages,
          MessagesType: data.MessagesType,
        });
      });
    }
    return dimensionData;
  }

  get nextProcessOptions(): IOption[]{
    let processData: IOption[] = [];
    processData.push({
      id: -3,
      value: 'please_select',
      disabled: false,
    });
    processData.push({
      id: -1,
      value: 'Tag Assign'
    })
    if (this.processList?.Processs && this.processList.Processs?.length > 0) {
      this.processList.Processs.map((process) => {
        processData.push({
          id: process.Id,
          value: process.ProcessName,
        });
      });
    }
    processData.push({
      id: -2,
      value: 'Tag Remove'
    })
    return processData;
  }
  //#endregion

  //#region Reset
  reset = () => {
    this.processList = GetAllProcessState;
  };

  resetProcessDetail = () => {
    this.process = undefined;
    this.processState = { ...this.initialStateValue };
  };

  resetDeleteProcess = () => {
    this.deleteProcessState = { ...this.initialStateValue };
  };

  resetAddUpdateProcessState = () => {
    this.addUpdateProcessState.inProgress = false;
    this.addUpdateProcessState.success = false;
    this.addUpdateProcessState.error = '';
  };

  resetSaveDimensionDataState = () => {
    this.saveDimensionDataState = { ...this.initialStateValue };
  };

  resetGetProcessDimensionDataState = () => {
    this.getProcessDimensionDataState = { ...this.initialStateValue };
  };
  //#endregion
}

export default new ProcessStore();
